import {
  App as AntdApp,
  Button,
  Empty,
  Flex,
  Popover,
  Space,
  Tag,
  Typography
} from 'antd'
import type { TableColumnsType } from 'antd'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type {
  PatchedPublicAccessCredentialUpdate,
  PublicAccessCredentialCreate,
  PublicAccessCredentialRead
} from 'src/api/main'
import {
  ContentSpinner,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  RevokeCredentialModal,
  RotateCredentialModal,
  TableActionColumnTitle
} from 'src/components'
import { useWorkspaceAppsList } from 'src/features/apps/hooks/useWorkspaceAppsList'
import { DEFAULT_PER_PAGE, DEFAULT_PER_PAGE_OPTIONS } from 'src/hooks'
import { paths } from 'src/router/paths'

import {
  CREDENTIAL_TOKEN_STATE_KEY,
  CredentialActionsCell,
  CredentialNameCell,
  CredentialStatusCell,
  isCredentialLocked,
  isCredentialRevoked,
  useCredentialRowClick,
  useCredentialsListStyles
} from '../../components/credentials'
import { WorkspacePageShell, useWorkspaceRoute } from '../WorkspacePageShell'

import { useWorkspacePublicAccessCredentials } from './hooks'
import { PublicAccessCredentialDrawer } from './PublicAccessCredentialDrawer'
import type { PublicAccessAction } from './types'

const { Text } = Typography

export const WorkspacePublicAccessCredentialsPage = () => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const { styles } = useCredentialsListStyles()
  const navigate = useNavigate()
  const { id, workspaceId } = useWorkspaceRoute()

  const creds = useWorkspacePublicAccessCredentials(workspaceId)
  // Core is a system application: hidden from the apps board, but bindable here.
  const { apps, isLoading: appsLoading } = useWorkspaceAppsList(workspaceId, {
    includeSystem: true
  })

  const [action, setAction] = useState<PublicAccessAction>(null)

  const closeAction = () => setAction(null)

  const goToDetail = (credentialId: string, token?: string | null) => {
    navigate(paths.workspaces.publicAccessDetail(id, credentialId), {
      state: token ? { [CREDENTIAL_TOKEN_STATE_KEY]: token } : undefined
    })
  }

  const handleCreate = async (body: PublicAccessCredentialCreate) => {
    const result = await creds
      .createCredential({ path: { organization_id: workspaceId }, body })
      .catch(() => null)
    if (!result) return
    closeAction()
    // Token is returned only once at create — carry it to the detail page
    // via router state; a reload clears it, matching the "show only once"
    // security model.
    goToDetail(result.id, result.token)
  }

  const handleUpdate = async (
    id: string,
    body: PatchedPublicAccessCredentialUpdate
  ) => {
    const result = await creds
      .updateCredential({ path: { id, organization_id: workspaceId }, body })
      .catch(() => null)
    if (result) closeAction()
  }

  const handleRevoke = async (reason: string | null) => {
    if (action?.kind !== 'revoke') return
    const result = await creds
      .revokeCredential({
        path: { id: action.credential.id, organization_id: workspaceId },
        body: { reason }
      })
      .catch(() => null)
    if (result) closeAction()
  }

  const handleRotate = async (expiresAt: string | null, revokeOld: boolean) => {
    if (action?.kind !== 'rotate') return
    const target = action.credential
    const result = await creds
      .rotateCredential({
        path: { id: target.id, organization_id: workspaceId },
        body: { expires_at: expiresAt, revoke_old_credential: revokeOld }
      })
      .catch(() => null)
    if (!result) return
    closeAction()
    // Rotate returns the new credential + its one-time token; the generated
    // type mistypes the response, so read the real runtime shape.
    const { new_credential: rotated } = result as {
      new_credential?: { id: string; token?: string | null }
    }
    if (rotated?.id) goToDetail(rotated.id, rotated.token)
    else message.success(t('workspace.publicAccess.messages.rotated'))
  }

  const rowClick = useCredentialRowClick<PublicAccessCredentialRead>((cid) =>
    goToDetail(cid)
  )

  const columns: TableColumnsType<PublicAccessCredentialRead> = [
    {
      title: t('workspace.publicAccess.columns.name'),
      key: 'name',
      fixed: 'left',
      width: 280,
      render: (_, item) => (
        <CredentialNameCell
          name={item.name}
          to={paths.workspaces.publicAccessDetail(id, item.id)}
        />
      )
    },
    {
      title: t('workspace.publicAccess.columns.apps'),
      key: 'apps',
      width: 140,
      render: (_, item) => {
        const count = item.apps?.length ?? 0
        if (!count) {
          return <Text type="secondary">—</Text>
        }
        return (
          <Popover
            content={
              <Space vertical size={4} style={{ maxWidth: 280 }}>
                {item.apps.map((binding) => (
                  <Flex
                    key={binding.saas_app_token}
                    gap={12}
                    justify="space-between"
                    align="center"
                  >
                    <Tag style={{ marginInlineEnd: 0 }}>{binding.app_code}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('workspace.publicAccess.bindings.permissionCount', {
                        count: binding.permission_codes.length
                      })}
                    </Text>
                  </Flex>
                ))}
              </Space>
            }
          >
            <Tag style={{ cursor: 'default' }}>
              {t('workspace.publicAccess.columns.appsCount', { count })}
            </Tag>
          </Popover>
        )
      }
    },
    {
      title: t('workspace.publicAccess.columns.status'),
      key: 'status',
      width: 160,
      render: (_, item) => (
        <CredentialStatusCell
          item={item}
          statusLabel={t(
            `workspace.publicAccess.status.${item.status ?? 'active'}`
          )}
          reasonTip={(reason) =>
            t('workspace.publicAccess.revokedReason', { reason })
          }
          atTip={(at) => t('workspace.publicAccess.revokedAt', { at })}
        />
      )
    },
    {
      title: t('workspace.publicAccess.columns.expires'),
      key: 'expires',
      width: 180,
      render: (_, item) => (
        <DateTimeCell
          value={item.expires_at}
          variant="date"
          empty={t('workspace.publicAccess.noExpiry')}
        />
      )
    },
    {
      title: t('workspace.publicAccess.columns.lastUsed'),
      key: 'lastUsed',
      width: 180,
      render: (_, item) => (
        <DateTimeCell
          value={item.last_used_at}
          variant="relative"
          empty={t('workspace.publicAccess.neverUsed')}
        />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, item) => (
        <CredentialActionsCell
          locked={isCredentialLocked(item.status)}
          revoked={isCredentialRevoked(item.status)}
          onEdit={() => setAction({ kind: 'edit', credential: item })}
          onRotate={() => setAction({ kind: 'rotate', credential: item })}
          onRevoke={() => setAction({ kind: 'revoke', credential: item })}
          rotateLabel={t('workspace.publicAccess.rotate')}
          revokeLabel={t('workspace.publicAccess.revoke')}
        />
      )
    }
  ]

  return (
    <WorkspacePageShell
      sectionTitle={t('workspace.publicAccess.sectionTitle')}
      sectionDescription={t('workspace.publicAccess.sectionDescription')}
    >
      {creds.isError ? (
        <QueryErrorAlert onRetry={() => void creds.refetch()} />
      ) : creds.isLoading ? (
        <ContentSpinner minHeight={240} />
      ) : (
        <ProTableContainer<PublicAccessCredentialRead>
          showSearch={false}
          tableIdentifier="workspace-public-access-credentials"
          className={styles.table}
          tableLayout="fixed"
          rowKey="id"
          columns={columns}
          dataSource={creds.credentials}
          onRow={rowClick}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('workspace.publicAccess.empty')}
                  </Text>
                }
              />
            )
          }}
          pagination={{
            defaultPageSize: DEFAULT_PER_PAGE,
            pageSizeOptions: DEFAULT_PER_PAGE_OPTIONS.map(String),
            size: 'small',
            showSizeChanger: true,
            hideOnSinglePage: true
          }}
          scroll={{ x: 1000 }}
          leftChildren={
            <span className={styles.count}>
              <strong>{creds.credentials.length}</strong>{' '}
              <Text type="secondary">
                {t('workspace.publicAccess.toolbar.credentialsLabel')}
              </Text>
            </span>
          }
          rightChildren={
            <Button
              type="primary"
              size="small"
              icon={<Plus size={14} />}
              onClick={() => setAction({ kind: 'create' })}
            >
              {t('workspace.publicAccess.create')}
            </Button>
          }
        />
      )}

      <PublicAccessCredentialDrawer
        open={action?.kind === 'create' || action?.kind === 'edit'}
        mode={action?.kind === 'edit' ? 'edit' : 'create'}
        credential={action?.kind === 'edit' ? action.credential : null}
        apps={apps}
        appsLoading={appsLoading}
        submitting={creds.isCreating || creds.isUpdating}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onClose={closeAction}
      />

      <RotateCredentialModal
        open={action?.kind === 'rotate'}
        title={t('workspace.publicAccess.rotateConfirmTitle')}
        description={t('workspace.publicAccess.rotateConfirmBody')}
        okText={t('workspace.publicAccess.rotate')}
        loading={creds.isRotating}
        onConfirm={handleRotate}
        onCancel={closeAction}
      />

      <RevokeCredentialModal
        open={action?.kind === 'revoke'}
        title={t('workspace.publicAccess.revokeConfirmTitle')}
        description={t('workspace.publicAccess.revokeConfirmBody')}
        okText={t('workspace.publicAccess.revoke')}
        loading={creds.isRevoking}
        onConfirm={handleRevoke}
        onCancel={closeAction}
      />
    </WorkspacePageShell>
  )
}
